/**
 * `/replay/:id` — post-match VOD viewer (3D voxel).
 *
 * Loads `${replayBaseUrl}/{id}/match.json` for the battle index, then
 * fetches the first battle's JSONL frames and parses them with
 * `@pilot-protocol/replay-parser`. Renders via `<ReplayScene
 * mechMode="voxel">` with a playback HUD anchored bottom-left (same
 * vocabulary as `/live/:id`).
 *
 * Replaces the Astro static page + inline-script hydration with a
 * single React component.
 */

import { useCallback, useEffect, useState } from "react";
import type { CSSProperties } from "react";
import { useParams } from "react-router-dom";
import {
  DEFAULT_TOKENS,
  ReplayScene,
  tokensToCssVars,
} from "@pilot-protocol/visual-engine";
import {
  parseBattle,
  type BattleFrame,
  type MatchArchive,
  type ParsedBattle,
} from "@pilot-protocol/replay-parser";

const PLAY_TICK_MS = 600;

type ViewerState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | {
      kind: "loaded";
      archive: MatchArchive;
      battle: ParsedBattle;
      battleIndex: number;
    };

export default function ReplayPage() {
  const { id: matchId = "" } = useParams<{ id: string }>();
  const replayBaseUrl =
    (import.meta.env.PUBLIC_REPLAY_BASE_URL as string | undefined) ??
    "https://replays.pilotprotocol.gg";

  const [state, setState] = useState<ViewerState>({ kind: "loading" });
  const [tick, setTick] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!matchId) {
      setState({ kind: "error", message: "Missing match id." });
      return;
    }
    let cancelled = false;
    setState({ kind: "loading" });
    void loadArchive(matchId, replayBaseUrl, 0).then((next) => {
      if (!cancelled) {
        setState(next);
        setTick(0);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [matchId, replayBaseUrl]);

  // Auto-play.
  useEffect(() => {
    if (!playing || state.kind !== "loaded") return;
    const maxTurn = state.battle.states.length - 1;
    if (tick >= maxTurn) {
      setPlaying(false);
      return;
    }
    const id = setTimeout(() => setTick((t) => t + 1), PLAY_TICK_MS);
    return () => clearTimeout(id);
  }, [playing, tick, state]);

  const switchBattle = useCallback(
    async (idx: number) => {
      if (!matchId || state.kind !== "loaded") return;
      setState({ kind: "loading" });
      const next = await loadArchive(matchId, replayBaseUrl, idx, state.archive);
      setState(next);
      setTick(0);
      setPlaying(false);
    },
    [matchId, replayBaseUrl, state],
  );

  return (
    <div style={{ ...rootStyle, ...tokensToCssVars(DEFAULT_TOKENS) }}>
      {state.kind === "loaded" ? (
        <ReplayScene
          battle={state.battle}
          tick={tick}
          mechMode="voxel"
          stage={{ lighting: "magica-voxel" }}
          overlayPanels={["metadata", "statA", "statB"]}
          overlayBackground="transparent"
        />
      ) : null}

      {state.kind === "loading" ? (
        <div style={messageStyle}>Loading replay…</div>
      ) : null}

      {state.kind === "error" ? (
        <div style={messageStyle} role="alert">
          {state.message}
        </div>
      ) : null}

      {state.kind === "loaded" ? (
        <div style={hudStyle}>
          <p style={kickerStyle}>Replay</p>
          <p style={matchIdStyle}>{matchId}</p>
          <PlaybackBar
            tick={tick}
            maxTick={state.battle.states.length - 1}
            playing={playing}
            onTick={setTick}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          />
          {state.archive.battles && state.archive.battles.length > 1 ? (
            <div style={battleRowStyle}>
              <span style={mutedStyle}>Battles:</span>
              {state.archive.battles.map((_b, idx) => (
                <button
                  type="button"
                  key={idx}
                  onClick={() => switchBattle(idx)}
                  style={
                    idx === state.battleIndex
                      ? activeChipStyle
                      : chipStyle
                  }
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function PlaybackBar({
  tick,
  maxTick,
  playing,
  onTick,
  onPlay,
  onPause,
}: {
  tick: number;
  maxTick: number;
  playing: boolean;
  onTick: (t: number) => void;
  onPlay: () => void;
  onPause: () => void;
}) {
  return (
    <div style={playbackStyle}>
      <button
        type="button"
        style={chipStyle}
        onClick={() => onTick(Math.max(0, tick - 1))}
        disabled={tick <= 0}
      >
        ◀
      </button>
      {playing ? (
        <button type="button" style={chipStyle} onClick={onPause}>
          ❚❚
        </button>
      ) : (
        <button
          type="button"
          style={chipStyle}
          onClick={onPlay}
          disabled={tick >= maxTick}
        >
          ▶
        </button>
      )}
      <button
        type="button"
        style={chipStyle}
        onClick={() => onTick(Math.min(maxTick, tick + 1))}
        disabled={tick >= maxTick}
      >
        ▶
      </button>
      <input
        type="range"
        min={0}
        max={maxTick}
        value={tick}
        onChange={(e) => onTick(parseInt(e.target.value, 10))}
        style={sliderStyle}
        aria-label="Turn cursor"
      />
      <span style={tickReadoutStyle}>
        turn {tick} / {maxTick}
      </span>
    </div>
  );
}

async function loadArchive(
  matchId: string,
  replayBaseUrl: string,
  battleIndex: number,
  preloadedArchive?: MatchArchive,
): Promise<ViewerState> {
  try {
    let archive = preloadedArchive;
    if (!archive) {
      const res = await fetch(
        `${replayBaseUrl}/${encodeURIComponent(matchId)}/match.json`,
        { headers: { Accept: "application/json" } },
      );
      if (!res.ok) {
        return {
          kind: "error",
          message: `Replay not found (HTTP ${res.status}).`,
        };
      }
      archive = (await res.json()) as MatchArchive;
    }
    const battleEntry = archive.battles?.[battleIndex];
    if (!battleEntry) {
      return { kind: "error", message: "No battles in this match." };
    }
    const battleRes = await fetch(
      `${replayBaseUrl}/${encodeURIComponent(matchId)}/${battleEntry.replay}`,
      { headers: { Accept: "application/x-ndjson" } },
    );
    if (!battleRes.ok) {
      return {
        kind: "error",
        message: `Battle JSONL fetch failed (HTTP ${battleRes.status}).`,
      };
    }
    const text = await battleRes.text();
    const frames: BattleFrame[] = [];
    for (const line of text.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      try {
        frames.push(JSON.parse(trimmed) as BattleFrame);
      } catch {
        // Truncated trailing line — benign for a viewer.
      }
    }
    const battle = parseBattle(frames);
    return { kind: "loaded", archive, battle, battleIndex };
  } catch (err) {
    return {
      kind: "error",
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

// ---------- Styles ----------

const rootStyle: CSSProperties = {
  position: "relative",
  width: "100%",
  height: "100%",
  background: "#050505",
  fontFamily: "var(--pp-ov-font-family)",
  color: "var(--pp-ov-text)",
};

const messageStyle: CSSProperties = {
  position: "absolute",
  inset: "50% 0 0 50%",
  transform: "translate(-50%, -50%)",
  padding: 24,
  border: "1px solid var(--pp-ov-border)",
  background: "var(--pp-ov-bg-strong)",
  color: "var(--pp-ov-text)",
};

const hudStyle: CSSProperties = {
  position: "absolute",
  bottom: 16,
  left: 16,
  zIndex: 10,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  padding: "10px 14px",
  background: "var(--pp-ov-bg-strong)",
  border: "1px solid var(--pp-ov-border)",
  maxWidth: "min(560px, 70vw)",
};

const kickerStyle: CSSProperties = {
  margin: 0,
  fontSize: "var(--pp-ov-font-sm)",
  textTransform: "uppercase",
  letterSpacing: "var(--pp-ov-tracking-caps)",
  color: "var(--pp-ov-text-muted)",
};

const matchIdStyle: CSSProperties = {
  margin: 0,
  fontSize: "var(--pp-ov-font-md)",
  color: "var(--pp-ov-text)",
  wordBreak: "break-all",
};

const playbackStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginTop: 4,
};

const battleRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  marginTop: 4,
};

const mutedStyle: CSSProperties = {
  fontSize: "var(--pp-ov-font-sm)",
  color: "var(--pp-ov-text-muted)",
  textTransform: "uppercase",
  letterSpacing: "var(--pp-ov-tracking-caps)",
};

const chipStyle: CSSProperties = {
  padding: "2px 8px",
  background: "transparent",
  border: "1px solid var(--pp-ov-border)",
  color: "var(--pp-ov-text)",
  fontFamily: "inherit",
  fontSize: "var(--pp-ov-font-sm)",
  cursor: "pointer",
};

const activeChipStyle: CSSProperties = {
  ...chipStyle,
  borderColor: "var(--pp-ov-text)",
  background: "var(--pp-ov-bg-strong)",
};

const sliderStyle: CSSProperties = {
  flex: 1,
  minWidth: 120,
  accentColor: "var(--pp-ov-text)",
};

const tickReadoutStyle: CSSProperties = {
  fontSize: "var(--pp-ov-font-sm)",
  color: "var(--pp-ov-text-muted)",
  fontVariantNumeric: "tabular-nums",
};
